import React, { useCallback, useMemo, useState } from 'react';
import { Storage } from 'aws-amplify';
import { Dimmer, Icon, Loader, Card, Button, Message } from 'semantic-ui-react';
import HelpText from 'components/HelpText/HelpText';
import Kbd from 'components/Kbd/Kbd';
import FileDropper from 'components/FileDropper/FileDropper';
import getUploadTypeDescription from 'modules/get-upload-type-description/get-upload-type-description';
import prepareFilename from 'modules/prepare-filename/prepare-filename';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import Label from 'components/Label/Label';
import { useAlert } from 'context/AlertProvider';
import { useUser } from 'hooks/redux/useUser';

export default function UploadShipments({ group }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const alert = useAlert();
  const {
    user: { email },
  } = useUser();

  const allowUpload = useMemo(
    () => process.env.REACT_APP_PREVENT_UPLOAD === 'false',
    [],
  );

  const MAX_FILESIZE = useMemo(() => {
    return process.env.REACT_APP_MAX_FILESIZE;
  }, []);

  const uploadFile = useCallback(async () => {
    try {
      setLoading(true);
      const preparedName = prepareFilename(files[0].name);
      await Storage.put(`${email}/${preparedName}`, files[0], {
        acl: 'bucket-owner-full-control',
      });

      alert.current.message(
        `Su fichero (renombrado a <strong>${preparedName}</strong>) se ha subido correctamente,
          en unos minutos recibirá un correo con los resultados
          <span>(el tiempo de espera dependerá del número de registros del fichero).</span>`,
        () => {
          setFiles([]);
        },
      );
    } catch (error) {
      alert.current.error(getMessageFromAxiosError(error));
    } finally {
      setLoading(false);
    }
  }, [alert, files, email]);

  const formIsValid = useCallback(() => {
    return files.length;
  }, [files]);

  const handleFileDeletion = useCallback(
    (name) => {
      const newFiles = files.filter((file) => file.name !== name);
      setFiles(newFiles);
    },
    [files],
  );

  const validateFiles = useCallback(
    (files) => {
      const isValid = files.every((file) => file.size <= MAX_FILESIZE);

      if (isValid) {
        setFiles(files);
      } else {
        alert.current.error(
          'El tamaño de archivo es superior al permitido (500Kb).',
        );
      }
    },
    [MAX_FILESIZE, alert],
  );

  return (
    <>
      <Dimmer active={loading} inverted>
        <Loader>Cargando…</Loader>
      </Dimmer>
      <Card.Header>
        <div>
          <Label>Fichero CSV o Excel</Label>
          <HelpText>
            <Icon name="info circle" /> El formato de archivo puede ser{' '}
            <Kbd>.csv</Kbd>, <Kbd>.xls</Kbd> o <Kbd>.xlsx</Kbd>
          </HelpText>
          <HelpText warning>
            <Icon name="warning sign" /> Por favor, ¡sube solo archivos{' '}
            <strong>{getUploadTypeDescription(group)}</strong>!
          </HelpText>
        </div>
        <div>
          <Button
            primary
            onClick={uploadFile}
            disabled={!formIsValid() || loading}
            loading={loading}
          >
            Subir archivo
          </Button>
        </div>
      </Card.Header>
      <Card.Content>
        {allowUpload ? (
          <FileDropper
            files={files}
            onAccept={validateFiles}
            onDeleteFile={handleFileDeletion}
            onAbort={(message) => alert.current.error(message)}
            onError={(message) => alert.current.error(message)}
          />
        ) : (
          <Message warning>
            La subida de archivos está restringida temporalmente…
          </Message>
        )}
      </Card.Content>
    </>
  );
}
